import { postRequest } from "../requests";
import { getRequestHeaders } from "../getRequestHeaders";
import { resetTurnstile } from "../utils";
import { handleResponse } from "../response";

export async function finishLogin(username: string, password: string, signInId: string, clientVerificationToken: string) {
  try {
    await postRequest('/identityserver/local/login', { Username: username, Password: password, SignInId: signInId, ClientVerificationToken: clientVerificationToken })
  } catch (error: any) {
    if (error?.message) {
      throw Error(error.message, { cause: error });
    } else {
      throw Error("error-invalid-password", { cause: error })
    }
  } finally {
    resetTurnstile();    
  }
}

export async function startJoin(email: string, firstName: string, lastName: string) {
  try {
      await postRequest('api/ResearchKit/Registration/RegisterCoordinator', { Email: email, FirstName: firstName, LastName: lastName }, true)
  } catch (error: any) {
    if (error?.message) {
      throw Error(error.message, { cause: error });
    } else {
      throw Error("error-registration-failed", { cause: error })
    }
  } finally {
    resetTurnstile();
  }
}

export async function forgotPassword(signInId: string, userName: string) {
  try {
    const response = await postRequest(`api/forgotpassword/ForgotPassword?signInId=${encodeURIComponent(signInId)}`, userName, true)
    return response
  } catch (error) {
    throw Error("error-forgot-password-failed", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function resetPassword(userName: string, password: string, nonce: string) {
  let response;
  try {
    response = await postRequest("~/identityserver/local/ResetPassword", {
      userName,
      newPasswordViewModel: {
        newPassword: password,
        confirmNewPassword: password,
      },
      nonce
    })
  } catch (error) {
    throw Error("error-reset-password-failed", { cause: error })
  }
  if (!response?.success) {
    throw Error(response.reason)
  }
  return response
}

export async function verifyNonce(email: string, nonce: string) {
  const errorMsg = "error-invalid-password-reset-nonce";
  let response;
  try {
    const params = { email, nonce };
    response = await postRequest(`api/activation/validatenonce?${new URLSearchParams(params)}`, {})
  } catch (error) {
    throw Error(errorMsg, { cause: error })
  }
  if (!response) {
    throw Error(errorMsg)
  }
  return response
}

export async function validatePasswordRequirements(email: string, password: string) {
  const errorMsg = "error-validate-password-requirements-failed";
  let response;
  try {
    response = await postRequest(`api/activation/validatePasswordRequirements?${new URLSearchParams({ email })}`, password)
  } catch (error) {
    throw Error(errorMsg, { cause: error })
  }
  return response
}

export async function initializePassword(userName: string, password: string, nonce: string) {
  let response;
  try {
    response = await postRequest("identityserver/local/InitializePassword", {
      userName,
      newPasswordViewModel: {
        newPassword: password,
        confirmNewPassword: password,
      },
      nonce
    })
  } catch (error) {
    throw Error("error-initialize-password-failed", { cause: error })
  }
  if (!response?.success) {
    throw Error(response.reason)
  }
  return response
}

export async function snooze2fa() {
    try {
        const response = await postRequest("identityserver/local/Snooze2FA", {})
        return response
    } catch (error) {
        throw Error("error-snooze-2fa-failed", { cause: error })
    }
}

export async function enableTotp2fa(key: string, confirmationCode: string) {
  try {
    const response = await postRequest("identityserver/local/EnableTwoStepVerification", {
      key,
      confirmationCode
    })
    return response
  } catch (error) {
    throw Error("error-enable-totp-2fa-failed", { cause: error })
  }
}

export async function verifyTotp2fa(code: string, clientVerificationToken: string, rememberMe?: boolean) {
  try {
    const response = await postRequest("identityserver/local/ProvideTotp", {
      code,
      clientVerificationToken,
      rememberMe
    })
    return response
  } catch (error) {
    throw Error("error-verify-totp-2fa-failed", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function generateRecoveryCodes() {
  try {
    const response = await postRequest("identityserver/local/GenerateRecoveryCodes", {})
    return response
  } catch (error) {
    throw Error("error-generate-recovery-codes-failed", { cause: error })
  }
}

export async function verifyRecoveryCode(code: string, clientVerificationToken: string, rememberMe?: boolean) {
  try {
    const response = await postRequest("identityserver/local/ProvideRecoveryCode", {
      code,
      clientVerificationToken,
      rememberMe
    })
    return response
  } catch (error) {
    throw Error("error-verify-totp-2fa-failed", { cause: error })
  } finally {
    resetTurnstile();
  }
}

export async function getPasskeyRegistrationOptions(baseURL: string) {

  try {
    const response = await fetch(`${baseURL}/identityserver/core/passkeys/registration-options`, {
      credentials: "include",
      headers: getRequestHeaders('json')
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-get-passkey-registration-options-failed", { cause: error })
  }
}

export async function addPasskey(baseURL: string, payload: any) {

  try {
    const response = await fetch(`${baseURL}/identityserver/core/passkeys/add`, {
      method: 'POST',
      credentials: "include",
      headers: getRequestHeaders('json'),
      body: JSON.stringify(payload),
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-add-passkey-failed", { cause: error })
  }
}

export async function getPasskeyAuthenticationOptions(baseURL: string) {

  try {
    const response = await fetch(`${baseURL}/identityserver/core/passkeys/authentication-options`, {
      credentials: "include",
      headers: getRequestHeaders('json')
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-get-passkey-authentication-options-failed", { cause: error })
  }
}

export async function verifyPasskey(baseURL: string, payload: any) {

  try {
    const response = await fetch(`${baseURL}/identityserver/core/passkeys/verify`, {
      method: 'POST',
      credentials: "include",
      headers: getRequestHeaders('json'),
      body: JSON.stringify(payload),
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-verify-passkey-failed", { cause: error })
  }
}

export async function getPasskeys(baseURL: string) {

  try {
    const response = await fetch(`${baseURL}/apicore/passkeys`, {
      credentials: "include",
      headers: getRequestHeaders('json')
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-get-passkeys-failed", { cause: error })
  }
}

export async function savePasskey(baseURL: string, passkey: { id: string, description: string }) {

  try {
    const response = await fetch(`${baseURL}/apicore/passkeys?id=${encodeURIComponent(passkey.id)}`, {
      method: 'PUT',
      credentials: "include",
      headers: getRequestHeaders('json'),
      body: JSON.stringify(passkey)
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-save-passkey-failed", { cause: error })
  }
}

export async function deletePasskey(baseURL: string, id: string) {

  try {
    const response = await fetch(`${baseURL}/apicore/passkeys?id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: "include",
      headers: getRequestHeaders('json')
    })
    return await handleResponse(response)
  } catch (error) {
    throw Error("error-delete-passkey-failed", { cause: error })
  }
}
